import moment from 'moment';
import { FunctionComponent, JSX, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import {
  Table,
  Box,
  Title,
  SubTitle,
  Button,
  Badge,
  Description,
  TransactionsLists,
  TableProps,
  capitalizeFirstLetter,
  GetColorStatus,
  formattedCurrency,
} from 'vopay';

type FC = FunctionComponent;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type DataProps = {
  FundingTransactionID: string;
  AccountTransferID: string;
  AccountTransferIDs: {
    [key: string]: {
      ID: string;
    };
  };
  ParentAccountName: string;
};

const FundTransferDetails: FC = () => {
  const history = useHistory();
  const { onSubNavClick } = useNavMenu();
  const { setOpenModule } = useOpenModule();
  const { state } = useLocation<DataProps>();
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [transferIds, setTransferIds] = useState<string[]>([]);
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.TransferFunds.FundTransferDetails' });
  const { t: tGlobal } = useTranslation('translations');

  useEffect(() => {
    if (state.FundingTransactionID && state.AccountTransferID) {
      const transactionIDs = [state.FundingTransactionID];

      if (state.AccountTransferID !== '') {
        transactionIDs.push(state.AccountTransferID);
        setTransferIds([state.AccountTransferID]);
      } else if (Object.keys(state.AccountTransferIDs).length !== 0) {
        const ids = Object.values(state.AccountTransferIDs).map((item) => item.ID);
        setTransferIds(ids);
        transactionIDs.push(...ids);
      }

      fetchTransactionsHistory({
        StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
        EndDateTime: moment().utc().format('YYYY-MM-DD'),
        TransactionID: transactionIDs.join(','),
      });
    }
  }, []);

  return (
    <>
      <Box mb="md">
        <Title title={t('title')} />
      </Box>

      <Box pt="md" pb="md">
        <SubTitle subTitle={t('fund') || ''} />
      </Box>
      {transactions
        .filter((subTransaction: TransactionsLists) => subTransaction.TransactionID === state.FundingTransactionID)
        .map((subTransaction: TransactionsLists, index: number) => (
          <Box key={index} mt="md" flx="col">
            <Description bold>{t('transactionDetails')}</Description>
            <Table {...createFundDetails(subTransaction)} />
          </Box>
        ))}

      <Box pt="md" pb="md">
        <SubTitle subTitle={t('transfer') || ''} />
      </Box>
      {transactions
        .filter((subTransaction: TransactionsLists) => transferIds.includes(subTransaction.TransactionID))
        .map((subTransaction: TransactionsLists, index: number) => (
          <Box key={index} mt="md" flx="col">
            <Description bold>{t('transactionDetailsMainAccount')}</Description>
            <Table {...createTransferDetails(subTransaction)} />
          </Box>
        ))}
    </>
  );

  function createFundDetails(subTransaction: TransactionsLists): TableProps {
    const transactionDetailsPairs: KeyValuePair[] = [
      {
        display: t('transactionID'),
        value: (
          <Box gap="md" ai="center">
            {subTransaction.TransactionID}
            <Button
              id="fund-transaction-id"
              label={t('details')}
              color="secondary"
              size="sm"
              onClick={() => {
                setOpenModule('statements');
                onSubNavClick('transactionsHistory', '/transactions-history', t('transactionsHistory'));
                history.push({
                  pathname: `/transactions-history/details/EFTFunding/${subTransaction.TransactionID}/${subTransaction.Currency}`,
                  state: { transactionId: subTransaction.TransactionID },
                });
              }}
            />
          </Box>
        ),
      },
      {
        display: t('transactionStatus'),
        value: (
          <Badge
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${subTransaction.TransactionStatus}`, {
                defaultValue: subTransaction.TransactionStatus,
              }),
            )}
            hasIcon
            color={GetColorStatus('pending')}
          />
        ),
      },
      {
        display: t('transactionDate'),
        value: moment(subTransaction.TransactionDateTime).format('YYYY-MM-DD'),
      },
      {
        display: t('lastModified'),
        value: moment(subTransaction.LastModified).format('YYYY-MM-DD'),
      },
      {
        display: t('type'),
        value: t('fund') || '',
      },
      {
        display: t('amount'),
        value: `$${formattedCurrency(subTransaction.CreditAmount)}`,
      },
      {
        display: t('currency'),
        value: `${subTransaction.Currency}`,
      },
    ];

    const transactionDetailsProps: TableProps = {
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        detailKey: { header: t('transactionDetail') || '' },
        detailValue: { header: '' },
      },
      rows: transactionDetailsPairs.map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
    };

    return transactionDetailsProps;
  }

  function createTransferDetails(subTransaction: TransactionsLists): TableProps {
    const transactionDetailsPairs: KeyValuePair[] = [
      {
        display: t('transactionID'),
        value: (
          <Box gap="md" ai="center">
            {subTransaction.TransactionID}
            <Button
              id="fund-transaction-id"
              label={t('details')}
              color="secondary"
              size="sm"
              onClick={() => {
                setOpenModule('statements');
                onSubNavClick('transactionsHistory', '/transactions-history', t('transactionsHistory'));
                history.push({
                  pathname: `/transactions-history/details/AccountTransfer/${subTransaction.TransactionID}/${subTransaction.Currency}`,
                  state: { transactionId: subTransaction.TransactionID },
                });
              }}
            />
          </Box>
        ),
      },
      {
        display: t('transactionStatus'),
        value: (
          <Badge
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${subTransaction.TransactionStatus}`, {
                defaultValue: subTransaction.TransactionStatus,
              }),
            )}
            hasIcon
            color={GetColorStatus(subTransaction.TransactionStatus)}
          />
        ),
      },
      {
        display: t('type'),
        value: t('accountTransfer') || '',
      },
      {
        display: t('recipientAccount'),
        value: state.ParentAccountName,
      },
      {
        display: t('amount'),
        value: `$${formattedCurrency(subTransaction.DebitAmount)}`,
      },
      {
        display: t('currency'),
        value: `${subTransaction.Currency}`,
      },
    ];

    const transactionDetailsProps: TableProps = {
      name: 'transaction-details',
      variant: 'pair',
      columns: {
        detailKey: { header: t('transactionDetail') || '' },
        detailValue: { header: '' },
      },
      rows: transactionDetailsPairs.map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
    };

    return transactionDetailsProps;
  }
};

export default FundTransferDetails;
